import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "coachingOptions", "zoomSection", "placeSection",
    "dateRangeCheckbox", "dateRangeSection",
    "rsvpLimitCheckbox", "rsvpLimitSection",
    "rsvpMaxCheckbox", "rsvpMaxSection",
    "rsvpQuota", "rsvpQuotaTaken", "rsvpQuotaAvailable", "rsvpQuotaAvailableLabel",
    "rsvpQuotaNoTimeHelp", "rsvpMaxInput", "rsvpMaxPeriod"
  ]

  updateCoachingOptions() {
    const selected = this.coachingOptionsTarget.value

    if (this.hasZoomSectionTarget) {
      this.toggleSection(this.zoomSectionTarget, selected === "zoom")
    }
    if (this.hasPlaceSectionTarget) {
      this.toggleSection(this.placeSectionTarget, selected === "inperson")
    }
  }

  toggleDateRange() {
    const checked = this.dateRangeCheckboxTarget.checked
    this.toggleSection(this.dateRangeSectionTarget, checked)
  }

  toggleRsvpLimit() {
    const checked = this.rsvpLimitCheckboxTarget.checked
    this.toggleSection(this.rsvpLimitSectionTarget, checked)
  }

  toggleRsvpMax() {
    const checked = this.rsvpMaxCheckboxTarget.checked
    this.toggleSection(this.rsvpMaxSectionTarget, checked)
  }

  // Recalcula "tomados / disponibles" al cambiar el periodo o el limite. La ocupacion de
  // los cuatro periodos ya viene del servidor (data-occupancy): cambiar estos campos no
  // altera cuantas reservas existen, solo contra que numero se comparan. Por eso no hay
  // request aca — recien al guardar el server vuelve a contar.
  updateRsvpQuota() {
    if (!this.hasRsvpQuotaTarget) return

    const quota = this.rsvpQuotaTarget
    const period = this.hasRsvpMaxPeriodTarget ? this.rsvpMaxPeriodTarget.value : ""
    const key = period === "" ? "no_time" : period

    // Los badges solo tienen sentido con "Sin periodo": con dia/semana/mes muestran la
    // ocupacion del periodo vigente, que no informa sobre la capacidad de la actividad y
    // alarma sin motivo ("Sin cupos disponibles hoy" cuando manana vuelve a haber). Se
    // recalculan igual para que queden correctos si el usuario vuelve a "Sin periodo".
    quota.classList.toggle("hidden", period !== "")

    const occupancy = JSON.parse(quota.dataset.occupancy)
    const templates = JSON.parse(quota.dataset.availableTemplates)
    const variables = JSON.parse(quota.dataset.variables)

    const taken = occupancy[key] || 0
    const limit = this.hasRsvpMaxInputTarget ? parseInt(this.rsvpMaxInputTarget.value, 10) : 0
    const available = Math.max((Number.isNaN(limit) ? 0 : limit) - taken, 0)

    this.rsvpQuotaTakenTarget.textContent = this.formatQuota(
      quota.dataset.takenTemplate, { count: taken, max: Number.isNaN(limit) ? 0 : limit }
    )

    const pluralForm = available === 0 ? "zero" : (available === 1 ? "one" : "other")
    this.rsvpQuotaAvailableLabelTarget.textContent = this.formatQuota(
      templates[key][pluralForm], { count: available }
    )

    // El color se aplica intercambiando las clases del propio helper `badge`, que viajan
    // en data-variables. Asi no se hardcodean utilidades Tailwind en JS (podrian no estar
    // compiladas en el bundle) ni se duplica la paleta del helper.
    const variant = available === 0 ? "error" : (available <= 5 ? "warning" : "success")
    const badge = this.rsvpQuotaAvailableTarget
    Object.values(variables).forEach(classes => {
      classes.split(" ").forEach(cssClass => badge.classList.remove(cssClass))
    })
    variables[variant].split(" ").forEach(cssClass => badge.classList.add(cssClass))

    if (this.hasRsvpQuotaNoTimeHelpTarget) {
      this.rsvpQuotaNoTimeHelpTarget.classList.toggle("hidden", period !== "")
    }
  }

  formatQuota(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`%{${key}}`, value), template
    )
  }

  // Sincroniza visibilidad + estado `disabled` de los inputs internos de la seccion.
  // Disable es obligatorio: si queda enabled con el toggle apagado, el usuario puede
  // editarlo y al guardar el server lo descarta silenciosamente por paridad con React
  // (starts_at/ends_at/rsvp_limit/rsvp_max solo aplican con el toggle activo).
  toggleSection(target, show) {
    target.classList.toggle("hidden", !show)
    target.querySelectorAll("input, select, textarea").forEach(el => {
      el.disabled = !show
    })
  }
}
